import { agentsByNetworkQuery } from '@/graphql/queries';
import { useNetwork } from '@/hooks';
import { useQuery } from '@apollo/react-hooks';
import { Field } from '@availity/form';
import { SelectField } from '@availity/select';
import { useFormikContext } from 'formik';
import React, { useState } from 'react';
import { MoneyField } from '@/components';
import { FaChevronDown, FaChevronUp, FaTrash } from 'react-icons/fa';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Input,
  InputGroup,
  InputGroupAddon,
  Row,
} from 'reactstrap';

const MatchByGroups: React.FC = () => {
  const {
    values,
    setFieldValue,
    errors,
    touched,
    setFieldTouched,
  } = useFormikContext();
  const { network } = useNetwork();
  const groups = values?.autoMatchDeposit?.groups || [];

  // Track which cards are expanded
  const [openCards, setOpenCards] = useState<number[]>([]);

  // Toggle card expansion
  const toggleCard = (index: number) => {
    setOpenCards((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // Fetch all approved agents for the network
  const { data: agentsData } = useQuery(agentsByNetworkQuery, {
    variables: { networkID: network?.networkID },
  });

  // Get all selected agents across all groups
  const selectedAgentsInOtherGroups = (groupIndex: number) => {
    return groups.reduce((acc: string[], group, idx) => {
      // Skip the current group and groups without agents
      if (idx !== groupIndex && group.agents && group.agents.length > 0) {
        // Extract just the agentIDs from the selected agents
        return [...acc, ...group.agents.map((agent) => agent.value || agent)];
      }
      return acc;
    }, []);
  };

  const getAvailableAgentOptions = (groupIndex: number) => {
    const selectedAgents = selectedAgentsInOtherGroups(groupIndex);
    const allAgents =
      agentsData?.getApprovedAgentsByNetwork?.map((agent) => ({
        value: agent.agentID,
        label: `${agent.firstName} ${agent.lastName}`,
        firstName: agent.firstName,
        lastName: agent.lastName,
      })) || [];

    return allAgents.filter((agent) => !selectedAgents.includes(agent.value));
  };

  const addGroup = () => {
    setFieldValue('autoMatchDeposit.groups', [
      ...groups,
      {
        name: '',
        agents: [],
        percentage: '',
        minTransferAmount: undefined,
        maxTransferAmount: undefined,
        minDepositAmount: undefined,
      },
    ]);
    setFieldTouched('autoMatchDeposit.groups', false);
  };

  const removeGroup = (index) => {
    const updatedGroups = [...groups];
    updatedGroups.splice(index, 1);
    setFieldValue('autoMatchDeposit.groups', updatedGroups);

    // Check if the removed group was the default group
    if (values.autoMatchDeposit.defaultGroup === index) {
      // Reset defaultGroup to null
      setFieldValue('autoMatchDeposit.defaultGroup', null);
    } else if (
      values.autoMatchDeposit.defaultGroup !== null &&
      values.autoMatchDeposit.defaultGroup > index
    ) {
      // If a group before the default group was removed, adjust the index
      setFieldValue(
        'autoMatchDeposit.defaultGroup',
        values.autoMatchDeposit.defaultGroup - 1
      );
    }
  };

  const updateGroupName = (index, name) => {
    const updatedGroups = [...groups];
    updatedGroups[index].name = name;
    setFieldValue('autoMatchDeposit.groups', updatedGroups);
  };

  return (
    <Col>
      <div>
        {/* Main error message */}
        {errors?.autoMatchDeposit?.groups &&
          touched?.autoMatchDeposit?.groups &&
          typeof errors.autoMatchDeposit.groups === 'string' && (
            <div className="alert alert-danger mb-3">
              {errors.autoMatchDeposit.groups}
            </div>
          )}

        {/* Groups */}
        {groups.map((group, index) => (
          <Card key={index} className="mb-3">
            <CardHeader
              className="d-flex justify-content-between align-items-center cursor-pointer"
              onClick={() => toggleCard(index)}
            >
              <div className="d-flex align-items-center">
                {openCards.includes(index) ? (
                  <FaChevronUp />
                ) : (
                  <FaChevronDown />
                )}
                <h5 className="mb-0 ml-2">
                  {group.name
                    ? `Group #${index + 1}: ${group.name}`
                    : `Group #${index + 1}`}
                </h5>
              </div>
              <div>
                <Button
                  type="button"
                  color="danger"
                  size="sm"
                  className="mr-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeGroup(index);
                  }}
                >
                  <FaTrash /> Remove Group
                </Button>
              </div>
            </CardHeader>

            <Collapse isOpen={openCards.includes(index)}>
              <CardBody>
                <Row className="mb-3">
                  <Col xs={6}>
                    <Field
                      type="text"
                      name={`autoMatchDeposit.groups[${index}].name`}
                      className="form-control"
                      placeholder="Group Name"
                      value={group.name || ''}
                      onChange={(e) => updateGroupName(index, e.target.value)}
                      onBlur={() =>
                        setFieldTouched(
                          `autoMatchDeposit.groups[${index}].name`,
                          true
                        )
                      }
                    />
                  </Col>
                  <Col xs={3}>
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">%</InputGroupAddon>
                      <Input
                        type="number"
                        name={`autoMatchDeposit.groups[${index}].percentage`}
                        className="form-control"
                        placeholder="Percentage"
                        value={group.percentage || ''}
                        onChange={(e) => {
                          const updatedGroups = [...groups];
                          updatedGroups[index].percentage = Number(
                            e.target.value
                          );
                          setFieldValue(
                            'autoMatchDeposit.groups',
                            updatedGroups
                          );
                        }}
                        onBlur={() =>
                          setFieldTouched(
                            `autoMatchDeposit.groups[${index}].percentage`,
                            true
                          )
                        }
                        min="1"
                        max="100"
                      />
                    </InputGroup>
                    {errors?.autoMatchDeposit?.groups?.[index]?.percentage &&
                      touched?.autoMatchDeposit?.groups?.[index]
                        ?.percentage && (
                        <small className="text-danger">
                          {errors.autoMatchDeposit.groups[index].percentage}
                        </small>
                      )}
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={9}>
                    <SelectField
                      name={`autoMatchDeposit.groups[${index}].agents`}
                      options={getAvailableAgentOptions(index)}
                      isMulti
                      value={group.agents || []}
                      onChange={(selectedAgents) => {
                        const updatedGroups = [...groups];
                        updatedGroups[index].agents = selectedAgents || [];
                        setFieldValue('autoMatchDeposit.groups', updatedGroups);
                      }}
                      placeholder="Select agents..."
                    />
                  </Col>
                  <Col xs={3}>
                    <Button
                      type="button"
                      color="primary"
                      className="mb-2"
                      block
                      onClick={() => {
                        const availableAgents = getAvailableAgentOptions(index);
                        const updatedGroups = [...groups];
                        updatedGroups[index].agents = availableAgents.map(
                          (agent) => agent.value
                        );
                        setFieldValue('autoMatchDeposit.groups', updatedGroups);
                      }}
                    >
                      Select All Available
                    </Button>
                  </Col>
                </Row>

                <Row>
                  <Col xs={4}>
                    <MoneyField
                      name={`autoMatchDeposit.groups[${index}].minTransferAmount`}
                      label="Min Transfer Amount"
                      size="md"
                      placeholder={0}
                      minimum={0}
                    />
                  </Col>
                  <Col xs={4}>
                    <MoneyField
                      name={`autoMatchDeposit.groups[${index}].maxTransferAmount`}
                      label="Max Transfer Amount"
                      size="md"
                      placeholder={0}
                      minimum={0}
                    />
                  </Col>
                  <Col xs={4}>
                    <MoneyField
                      name={`autoMatchDeposit.groups[${index}].minDepositAmount`}
                      label="Min Deposit Amount"
                      size="md"
                      placeholder={0}
                      minimum={0}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Collapse>
          </Card>
        ))}

        <Row>
          <Col>
            <Button
              type="button"
              color="primary"
              onClick={addGroup}
              disabled={getAvailableAgentOptions(groups.length).length === 0}
              className="mb-3"
            >
              Add New Group
            </Button>
          </Col>
        </Row>
      </div>
    </Col>
  );
};

export default MatchByGroups;
